import type {
  AriaAttributes,
  KeyboardEvent,
  MouseEvent,
  PropsWithChildren,
} from 'react'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import ChevronLeft from '../icons/ChevronLeft'
import ChevronRight from '../icons/ChevronRight'
import FirstPage from '../icons/FirstPage'
import LastPage from '../icons/LastPage'
import NavigationItem from './NavigationItem'
import PageList from './PageList'

interface PaginationProps {
  'aria-label': AriaAttributes['aria-label']
  boundaryCount?: number
  count: number
  color?: PaginationColor
  defaultPage?: number
  disabled?: boolean
  siblingCount?: number
  size?: PaginationSize
  variant?: PaginationVariant
}

export type PaginationColor = 'default' | 'primary' | 'secondary'

export type PaginationSize = 'large' | 'medium' | 'small'

type PaginationSizes = {
  [key in PaginationSize]: string
}

export type PaginationVariant = 'filled' | 'outlined'

export type PaginationVariants = {
  [key in PaginationVariant]: {
    [key in PaginationColor]: {
      selected: string
      unselected: string
    }
  }
}

export const sizes: PaginationSizes = {
  large: 'min-w-10 h-10 px-1',
  medium: 'min-w-8 h-8 px-1',
  small: 'min-w-6 h-6 px-1',
}

export const variants: PaginationVariants = {
  filled: {
    default: {
      selected: `bg-black-10 focus-visible:ring`,
      unselected: `active:bg-black-25 focus:bg-black-15 focus-visible:ring hover:bg-black-15`,
    },
    primary: {
      selected: `bg-primary-main text-white-100 focus-visible:ring`,
      unselected: `active:bg-primary-darker active:text-white-100 focus:bg-primary-dark focus:text-white-100 focus-visible:ring hover:bg-primary-dark hover:text-white-100`,
    },
    secondary: {
      selected: `bg-secondary-main text-white-100 focus-visible:ring`,
      unselected: `active:bg-secondary-darker active:text-white-100 focus:bg-secondary-dark focus:text-white-100 focus-visible:ring hover:bg-secondary-dark hover:text-white-100`,
    },
  },
  outlined: {
    default: {
      selected: `border border-solid border-black-20 text-black-90 focus-visible:ring`,
      unselected: `active:bg-black-15 focus:bg-black-5 focus-visible:ring hover:bg-black-5`,
    },
    primary: {
      selected: `border border-solid border-primary-main text-primary-main focus-visible:ring`,
      unselected: `active:bg-primary-20 focus:bg-primary-10 focus-visible:ring hover:bg-primary-10`,
    },
    secondary: {
      selected: `border border-solid border-secondary-main text-secondary-main focus-visible:ring`,
      unselected: `active:bg-secondary-20 focus:bg-secondary-10 focus-visible:ring hover:bg-secondary-10`,
    },
  },
}

function Pagination({
  'aria-label': label,
  boundaryCount = 1,
  count,
  color = 'default',
  defaultPage = 1,
  disabled = false,
  siblingCount = 1,
  size = 'medium',
  variant = 'filled',
}: PropsWithChildren<PaginationProps>): JSX.Element {
  const intl = useIntl()
  const [selectedPage, selectPage] = useState(defaultPage)

  const handleSelectPage = (event: MouseEvent | KeyboardEvent) => {
    if (!disabled) {
      selectPage(parseInt((event.target as HTMLButtonElement).value))
    }
  }

  const handleMoveLeft = () => {
    if (selectedPage > 1 && !disabled) {
      selectPage(selectedPage - 1)
    }
  }

  const handleMoveRight = () => {
    if (selectedPage < count && !disabled) {
      selectPage(selectedPage + 1)
    }
  }

  const handleMoveFirst = () => {
    if (!disabled) {
      selectPage(1)
    }
  }

  const handleMoveLast = () => {
    if (!disabled) {
      selectPage(count)
    }
  }

  return (
    <nav aria-label={label}>
      <ul className="flex flex-wrap space-x-1 font-sans">
        <NavigationItem
          color={color}
          disabled={disabled || selectedPage === 1}
          key="coconut-pagination-first"
          label={intl.formatMessage({ id: 'Pagination.first' })}
          onClick={handleMoveFirst}
          size={size}
          variant={variant}
        >
          <FirstPage />
        </NavigationItem>
        <NavigationItem
          color={color}
          disabled={disabled || selectedPage === 1}
          key="coconut-pagination-previous"
          label={intl.formatMessage({ id: 'Pagination.previous' })}
          onClick={handleMoveLeft}
          size={size}
          variant={variant}
        >
          <ChevronLeft />
        </NavigationItem>
        <PageList
          boundaryCount={boundaryCount}
          color={color}
          count={count}
          disabled={disabled}
          onClick={handleSelectPage}
          page={selectedPage}
          siblingCount={siblingCount}
          size={size}
          variant={variant}
        />
        <NavigationItem
          color={color}
          disabled={disabled || selectedPage === count}
          key="coconut-pagination-next"
          label={intl.formatMessage({ id: 'Pagination.next' })}
          onClick={handleMoveRight}
          size={size}
          variant={variant}
        >
          <ChevronRight />
        </NavigationItem>
        <NavigationItem
          color={color}
          disabled={disabled || selectedPage === count}
          key="coconut-pagination-last"
          label={intl.formatMessage({ id: 'Pagination.last' })}
          onClick={handleMoveLast}
          size={size}
          variant={variant}
        >
          <LastPage />
        </NavigationItem>
      </ul>
    </nav>
  )
}

export default Pagination
